import { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, ArrowLeftRight, StickyNote, FileText, Eye, Download, X, Paperclip, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { CHK_STATUS, DD_CATEGORIES, DD_SIDES, sideLabel } from "@/lib/dataroom";

function isPreviewable(doc) {
  const ct = (doc.content_type || "").toLowerCase();
  const n = (doc.original_filename || "").toLowerCase();
  return ct === "application/pdf" || n.endsWith(".pdf") || ct.startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/.test(n);
}

function ChecklistRow({ item, canManage, canDownload, otherSideLabel, documents, docMap, onStatus, onSaveNote, onCopy, onDelete, onAttach, onDetach, onPreview, onDownload }) {
  const [note, setNote] = useState(item.notes || "");
  const [attachKey, setAttachKey] = useState(0);
  useEffect(() => { setNote(item.notes || ""); }, [item.notes]);
  const dirty = note !== (item.notes || "");

  const linkedIds = item.document_ids || [];
  const linkedDocs = linkedIds.map((id) => docMap[id]).filter(Boolean);
  const unlinkedDocs = documents.filter((d) => !linkedIds.includes(d.id));

  return (
    <div className="px-5 py-3" data-testid={`checklist-${item.id}`}>
      <div className="flex items-center gap-2">
        <span className="flex-1 text-sm text-slate-700">{item.title}</span>
        {canManage ? (
          <>
            <button onClick={() => onCopy(item)} title={`Kopier til ${otherSideLabel}`}
              className="p-2 rounded-lg text-slate-400 hover:text-brand-ink hover:bg-slate-100 transition-colors" data-testid={`checklist-copy-${item.id}`}>
              <ArrowLeftRight className="w-4 h-4" />
            </button>
            <Select value={item.status} onValueChange={(status) => onStatus(item.id, status)}>
              <SelectTrigger className="w-40 h-9" data-testid={`checklist-status-${item.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CHK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={() => { if (confirm(`Fjerne «${item.title}»?`)) onDelete(item.id); }}
              className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Fjern punkt" data-testid={`checklist-delete-${item.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        ) : (
          <Badge className={`border-0 ${CHK_STATUS[item.status]?.style}`}>{CHK_STATUS[item.status]?.label}</Badge>
        )}
      </div>

      {canManage && (
        <div className="mt-2 flex items-center gap-2">
          <StickyNote className="w-3.5 h-3.5 text-slate-300 shrink-0" />
          <Input value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => { if (dirty) onSaveNote(item.id, note); }}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            placeholder="Notat (f.eks. hvor dokumentet ligger)…" className="h-8 text-xs bg-slate-50 border-slate-200" data-testid={`checklist-note-${item.id}`} />
        </div>
      )}
      {!canManage && item.notes && (
        <p className="mt-1 flex items-start gap-1.5 text-xs text-slate-500">
          <StickyNote className="w-3.5 h-3.5 text-slate-300 shrink-0 mt-0.5" /> {item.notes}
        </p>
      )}

      {/* Linked documents */}
      {(linkedDocs.length > 0 || (canManage && unlinkedDocs.length > 0)) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {linkedDocs.map((doc) => (
            <span key={doc.id} data-testid={`checklist-doc-${item.id}-${doc.id}`}
              className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg bg-brand-limeSoft/60 border border-brand-lime/30 text-xs text-brand-ink max-w-[280px]">
              <FileText className="w-3.5 h-3.5 text-brand-limeDark shrink-0" />
              <span className="truncate">{doc.original_filename}</span>
              {isPreviewable(doc) && (
                <button onClick={() => onPreview(doc)} title="Forhåndsvis" className="p-0.5 rounded text-slate-500 hover:text-brand-ink" data-testid={`checklist-doc-preview-${doc.id}`}>
                  <Eye className="w-3.5 h-3.5" />
                </button>
              )}
              {canDownload && (
                <button onClick={() => onDownload(doc)} title="Last ned" className="p-0.5 rounded text-slate-500 hover:text-brand-ink" data-testid={`checklist-doc-download-${doc.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </button>
              )}
              {canManage && (
                <button onClick={() => onDetach(item.id, doc.id)} title="Fjern kobling" className="p-0.5 rounded text-slate-400 hover:text-rose-600" data-testid={`checklist-doc-detach-${item.id}-${doc.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}
          {canManage && unlinkedDocs.length > 0 && (
            <Select key={attachKey} onValueChange={(docId) => { onAttach(item.id, docId); setAttachKey((k) => k + 1); }}>
              <SelectTrigger className="h-7 w-auto gap-1 px-2 text-xs border-dashed text-slate-500" data-testid={`checklist-attach-${item.id}`}>
                <Paperclip className="w-3.5 h-3.5" /> Koble dokument
              </SelectTrigger>
              <SelectContent>
                {unlinkedDocs.map((d) => <SelectItem key={d.id} value={d.id}>{d.original_filename} <span className="text-slate-400">· {sideLabel(d.side || "kjoper")}</span></SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
}

export default function ChecklistTab({ deal, canManage }) {
  const qc = useQueryClient();
  const dealId = deal.id;
  const lockedSide = (deal.my_external && (deal.my_party === "kjoper" || deal.my_party === "selger")) ? deal.my_party : null;
  const [side, setSide] = useState(lockedSide || (deal.my_party === "selger" ? "selger" : "kjoper"));
  const [newTitle, setNewTitle] = useState("");
  const [newCategory, setNewCategory] = useState(DD_CATEGORIES[0]);
  const [preview, setPreview] = useState(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["deal-checklist", dealId],
    queryFn: async () => (await apiClient.get(`/deals/${dealId}/checklist`)).data,
  });

  const { data: documents = [] } = useQuery({
    queryKey: ["deal-docs", dealId, "all"],
    queryFn: async () => (await apiClient.get(`/deals/${dealId}/documents`)).data,
  });
  const docMap = useMemo(() => Object.fromEntries(documents.map((d) => [d.id, d])), [documents]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["deal-checklist", dealId] });
    qc.invalidateQueries({ queryKey: ["deal", dealId] });
    qc.invalidateQueries({ queryKey: ["deal-activity", dealId] });
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/deals/${dealId}/checklist/${id}`, data),
    onSuccess: invalidate,
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke oppdatere"),
  });

  const addMutation = useMutation({
    mutationFn: (payload) => apiClient.post(`/deals/${dealId}/checklist`, payload),
    onSuccess: () => { invalidate(); setNewTitle(""); toast.success("Punkt lagt til"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke legge til"),
  });

  const copyMutation = useMutation({
    mutationFn: (payload) => apiClient.post(`/deals/${dealId}/checklist`, payload),
    onSuccess: (_res, payload) => { invalidate(); toast.success(`Kopiert til ${DD_SIDES.find((s) => s.key === payload.side)?.label}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke kopiere"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => apiClient.delete(`/deals/${dealId}/checklist/${id}`),
    onSuccess: () => { invalidate(); toast.success("Punkt fjernet"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke fjerne"),
  });

  const attachMutation = useMutation({
    mutationFn: ({ itemId, documentId }) => apiClient.post(`/deals/${dealId}/checklist/${itemId}/documents`, { document_id: documentId }),
    onSuccess: () => { invalidate(); toast.success("Dokument koblet til punktet"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke koble dokument"),
  });

  const detachMutation = useMutation({
    mutationFn: ({ itemId, docId }) => apiClient.delete(`/deals/${dealId}/checklist/${itemId}/documents/${docId}`),
    onSuccess: () => { invalidate(); toast.success("Kobling fjernet"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke fjerne kobling"),
  });

  const handleDownload = async (doc) => {
    try {
      const res = await apiClient.get(`/deals/${dealId}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || "fil";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      qc.invalidateQueries({ queryKey: ["deal-activity", dealId] });
    } catch { toast.error("Nedlasting feilet"); }
  };

  const openPreview = async (doc) => {
    setPreview({ loading: true, name: doc.original_filename });
    try {
      const res = await apiClient.get(`/deals/${dealId}/documents/${doc.id}/preview`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const ct = (doc.content_type || "").toLowerCase();
      const isImg = ct.startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/.test((doc.original_filename || "").toLowerCase());
      setPreview({ url, type: isImg ? "image" : "pdf", name: doc.original_filename });
      qc.invalidateQueries({ queryKey: ["deal-activity", dealId] });
    } catch { setPreview(null); toast.error("Kunne ikke åpne forhåndsvisning"); }
  };
  const closePreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); };

  const sideItems = useMemo(() => items.filter((it) => (it.side || "kjoper") === side), [items, side]);
  const grouped = useMemo(() => {
    const g = {};
    sideItems.forEach((it) => { (g[it.category] = g[it.category] || []).push(it); });
    return g;
  }, [sideItems]);

  const sideCount = (key) => items.filter((it) => (it.side || "kjoper") === key).length;
  const approved = sideItems.filter((i) => i.status === "approved").length;
  const pct = sideItems.length ? Math.round((approved / sideItems.length) * 100) : 0;
  const sideLabel = DD_SIDES.find((s) => s.key === side)?.label;
  const otherSide = side === "kjoper" ? "selger" : "kjoper";
  const otherSideLabel = DD_SIDES.find((s) => s.key === otherSide)?.label;
  const canDownload = deal.my_permission === "manage" || deal.my_permission === "download";

  const handleAdd = () => {
    const title = newTitle.trim();
    if (!title) { toast.error("Skriv inn en tittel"); return; }
    addMutation.mutate({ title, category: newCategory, side });
  };
  const handleCopy = (item) => copyMutation.mutate({ title: item.title, category: item.category, side: otherSide, notes: item.notes || "" });

  if (isLoading) return <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-14 bg-white rounded-xl animate-pulse" />)}</div>;

  return (
    <div className="space-y-6">
      {lockedSide ? (
        <div className="inline-flex items-center gap-2 px-4 h-9 rounded-xl bg-brand-ink text-white text-sm font-semibold" data-testid="checklist-locked-side">
          <Lock className="w-3.5 h-3.5 text-brand-lime" /> Din side: {DD_SIDES.find((s) => s.key === lockedSide)?.label}
        </div>
      ) : (
        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white border border-slate-200 shadow-sm" data-testid="checklist-side-toggle">
          {DD_SIDES.map((s) => (
            <button key={s.key} onClick={() => setSide(s.key)} data-testid={`checklist-side-${s.key}`} data-active={side === s.key}
              className={cn("px-4 h-9 rounded-lg text-sm font-semibold transition-colors", side === s.key ? "bg-brand-ink text-white" : "text-slate-500 hover:text-brand-ink")}>
              {s.label} <span className="opacity-70 font-normal">({sideCount(s.key)})</span>
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200/70 p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="font-semibold text-brand-ink">Fremdrift · {sideLabel}</p>
          <span className="text-sm text-slate-500" data-testid="checklist-progress-count">{approved} av {sideItems.length} godkjent</span>
        </div>
        <Progress value={pct} className="h-2" />
      </div>

      {canManage && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-4" data-testid="checklist-add-row">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Legg til punkt · {sideLabel}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
              placeholder="Tittel på dokument/punkt…" className="flex-1 h-10" data-testid="checklist-new-title" />
            <Select value={newCategory} onValueChange={setNewCategory}>
              <SelectTrigger className="w-full sm:w-56 h-10" data-testid="checklist-new-category"><SelectValue /></SelectTrigger>
              <SelectContent>{DD_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={handleAdd} disabled={addMutation.isPending} className="h-10 shrink-0" data-testid="checklist-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Legg til
            </Button>
          </div>
        </div>
      )}

      {sideItems.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-10 text-center text-slate-500">
          Ingen sjekklistepunkter for {sideLabel} ennå.
        </div>
      ) : (
        Object.entries(grouped).map(([category, list]) => (
          <div key={category} className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 text-sm font-semibold text-brand-ink">{category}</div>
            <div className="divide-y divide-slate-100">
              {list.map((item) => (
                <ChecklistRow
                  key={item.id}
                  item={item}
                  canManage={canManage}
                  canDownload={canDownload}
                  otherSideLabel={otherSideLabel}
                  documents={documents}
                  docMap={docMap}
                  onStatus={(id, status) => updateMutation.mutate({ id, data: { status } })}
                  onSaveNote={(id, notes) => updateMutation.mutate({ id, data: { notes } })}
                  onCopy={handleCopy}
                  onDelete={(id) => deleteMutation.mutate(id)}
                  onAttach={(itemId, documentId) => attachMutation.mutate({ itemId, documentId })}
                  onDetach={(itemId, docId) => detachMutation.mutate({ itemId, docId })}
                  onPreview={openPreview}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          </div>
        ))
      )}

      <Dialog open={!!preview} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-5xl w-[95vw] h-[88vh] flex flex-col p-0 gap-0" data-testid="checklist-preview-dialog">
          <DialogHeader className="px-5 py-3 border-b border-slate-100">
            <DialogTitle className="text-brand-ink text-base truncate pr-8">{preview?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100 flex items-center justify-center overflow-auto">
            {preview?.loading ? (
              <p className="text-slate-500 text-sm">Laster forhåndsvisning…</p>
            ) : preview?.type === "image" ? (
              <img src={preview.url} alt={preview.name} className="max-w-full max-h-full object-contain" />
            ) : preview?.url ? (
              <iframe src={preview.url} title={preview.name} className="w-full h-full" />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
